import { pipe } from "fp-ts/lib/pipeable";
import { array } from "fp-ts";
import {
    TCaseTimelineEntry,
    TCaseTimelineMilestoneEntry,
    TCaseTimelineRoadblockContext,
    TCaseTimelineRoadblockEntry,
    isCaseTimelineMilestoneEntry,
} from "../../../../domain/codecs/form/CaseTimelineForm";
import { TChatMessageAction } from "../../../../domain/codecs/form/RoadblockForm";
import { areISODatesNoMoreThanNUnitsApart } from "../../../../shared/src/utilsByDomain/dateTime/areISODatesNoMoreThanNUnitsApart";
import { areISODatesOnTheSameDay } from "../../../../shared/src/utilsByDomain/dateTime/areISODatesOnTheSameDay";

export type TTimelineFilter = "notes" | "all";

export type TTimelineNoteCluster = {
    kind: "note";
    date: string;
    userID: string | null;
    roadblock: TCaseTimelineRoadblockContext;
    entries: Array<TCaseTimelineRoadblockEntry>;
};

export type TTimelineEvent = {
    kind: "event";
    date: string;
    roadblock: TCaseTimelineRoadblockContext;
    entry: TCaseTimelineRoadblockEntry;
};

export type TTimelineMilestone = {
    kind: "milestone";
    date: string;
    entry: TCaseTimelineMilestoneEntry;
};

export type TTimelineDay = {
    kind: "day";
    date: string;
};

export type TTimelineRow =
    | TTimelineDay
    | TTimelineNoteCluster
    | TTimelineEvent
    | TTimelineMilestone;

export type TCaseTimelineRows = {
    pinned: Array<TTimelineNoteCluster>;
    rows: Array<TTimelineRow>;
};

const isMessageEntry = (entry: TCaseTimelineRoadblockEntry): boolean => entry.tag === "message";

type TPinnedNoteEntry = TChatMessageAction & { roadblock: TCaseTimelineRoadblockContext };
const isPinnedMessageEntry = (entry: TCaseTimelineEntry): entry is TPinnedNoteEntry =>
    entry.tag === "message" && entry.action.pinned_at !== null;

const messageClusterFits = (cluster: TTimelineNoteCluster, entry: TCaseTimelineRoadblockEntry): boolean =>
    cluster.roadblock.roadblock_id === entry.roadblock.roadblock_id
    && cluster.userID === entry.action.created_by
    && areISODatesNoMoreThanNUnitsApart({
        unit: "minutes",
        amount: 5,
        dateA: cluster.date,
        dateB: entry.action_date,
    });

const toItems = (entries: Array<TCaseTimelineEntry>): Array<TTimelineNoteCluster | TTimelineEvent | TTimelineMilestone> =>
    pipe(
        entries,
        array.reduce<TCaseTimelineEntry, Array<TTimelineNoteCluster | TTimelineEvent | TTimelineMilestone>>(
            [],
            (items, entry) => {
                if (isCaseTimelineMilestoneEntry(entry)) {
                    return items.concat({ kind: "milestone", date: entry.action_date, entry });
                }

                if (isMessageEntry(entry)) {
                    const last = items[items.length - 1];
                    if (last !== undefined && last.kind === "note" && messageClusterFits(last, entry)) {
                        return items.slice(0, -1).concat({ ...last, entries: last.entries.concat(entry) });
                    }
                    return items.concat({
                        kind: "note",
                        date: entry.action_date,
                        userID: entry.action.created_by,
                        roadblock: entry.roadblock,
                        entries: [entry],
                    });
                }

                return items.concat({
                    kind: "event",
                    date: entry.action_date,
                    roadblock: entry.roadblock,
                    entry,
                });
            },
        ),
    );

const buildPinnedBand = (entries: Array<TCaseTimelineEntry>): Array<TTimelineNoteCluster> =>
    entries
        .filter(isPinnedMessageEntry)
        .sort((a, b) => (b.action.pinned_at ?? "").localeCompare(a.action.pinned_at ?? ""))
        .map((entry): TTimelineNoteCluster => ({
            kind: "note",
            date: entry.action_date,
            userID: entry.action.created_by,
            roadblock: entry.roadblock,
            entries: [entry],
        }));

export const buildTimelineRows = (entries: Array<TCaseTimelineEntry>, filter: TTimelineFilter): TCaseTimelineRows => {
    const items = toItems(entries);

    const days = pipe(
        items,
        array.reduce<typeof items[number], Array<{ date: string; items: typeof items }>>(
            [],
            (acc, item) => {
                const currentDay = acc[acc.length - 1];
                if (currentDay === undefined || !areISODatesOnTheSameDay(currentDay.date, item.date)) {
                    return acc.concat({ date: item.date, items: [item] });
                }
                return acc.slice(0, -1).concat({ ...currentDay, items: currentDay.items.concat(item) });
            },
        ),
    );

    const rows = pipe(
        [...days].reverse(),
        array.chain((day): Array<TTimelineRow> => {
            const visible = filter === "all"
                ? day.items
                : day.items.filter((item) => item.kind !== "event");

            if (visible.length === 0) {
                return [];
            }

            const dayRow: TTimelineRow = { kind: "day", date: day.date };
            return [dayRow, ...[...visible].reverse()];
        }),
    );

    return { pinned: buildPinnedBand(entries), rows };
};
